import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchData, postData } from "../func/api";
import { useAuth } from "../contexts/AuthContext";
import { APPROVAL_STATUS, ROLE_UUID, REPORTING_TIME } from "../const/config";
import { getApprovalStatus } from "../func/status_utils";
import { shouldValidateSituation } from '../func/util';

export const useFirstReportLogic = () => {
    const { uuid } = useParams();
    const { currentUser } = useAuth();

    // 状態管理
    const [earthquake, setEarthquake] = useState(null);
    const [checkResult, setCheckResult] = useState([]);
    const [approval, setApproval] = useState([]);
    const [reportDetailList, setReportDetailList] = useState([]);
    const [mandatoryFacilitys, setMandatoryFacilitys] = useState([]);
    const [facilityDams, setFacilityDams] = useState([]);
    const [mailInfo, setMailInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [createComplete, setCreateComplete] = useState(false);
    const [department, setDepartment] = useState([]);
    const [taskStatus, setTaskStatus] = useState(() => {
        const saved = localStorage.getItem("taskStatus");
        if (saved !== null && Number.isFinite(Number(saved))) {
            return Number(saved);
        }
        // savedが null の場合や、数値として無効な文字列だった場合にデフォルト値の12を返す(新規ブラウザで開い場合に発生する)
        return 12;
    });

    // 派生状態
    const approvalStatus = useMemo(() => getApprovalStatus(approval), [approval]);
    const isInputter = approvalStatus === APPROVAL_STATUS.REMAND || approvalStatus === APPROVAL_STATUS.INIT;
    const isReviewer = approvalStatus === APPROVAL_STATUS.UNAPPROVED && currentUser?.role !== ROLE_UUID.VIEWER;
    const isReadOnly = approvalStatus === APPROVAL_STATUS.APPROVED || approvalStatus === APPROVAL_STATUS.UNAPPROVED;
    const isApprovedNotSent = approvalStatus === APPROVAL_STATUS.APPROVED && !earthquake?.initial_report_sent_at;
    const isSent = !!earthquake?.initial_report_sent_at;
    const isValidTask = taskStatus >= 12;

    // ハンドラー
    const handleRestart = useCallback(async () => {
        try {
            localStorage.setItem("taskStatus", "0");
            await postData("api/post_report_stop", {
                earthquake_uuid: earthquake.uuid,
                initial_report_uuid: earthquake.initial_report_uuid,
                user_uuid: currentUser.id,
            });
            window.location.href = "/initial_task";
        } catch (e) {
            console.error(e);
        }
    }, [earthquake, currentUser]);

    // データ取得ロジック
    useEffect(() => {
        if (!uuid) return;
        const fetchAll = async () => {
            setLoading(true);
            try {
                const eq = await fetchData(`api/get_earthquake/${uuid}`);
                setEarthquake(eq);
                // API呼び出しと状態更新のロジック
                const [cr, ap, mf, fd, dl, dp] = await Promise.all([
                    fetchData(`api/get_check_result/${uuid}?is_init=${eq[`initial_report_uuid`] === null}`),
                    fetchData(`api/get_initial_report_approval/${uuid}`),
                    fetchData('api/get_mandatory_facility'),
                    fetchData('api/get_facility_dam'),
                    fetchData(`api/get_dam_report_detail_list/${uuid}`),
                    fetchData(`api/get_department`)
                ]);
                setApproval(ap);
                setReportDetailList(dl);

                // ステータス取得
                const status = getApprovalStatus(ap);
                const mandatory_facility_uuids = mf.map(facility => facility.uuid);
                setMandatoryFacilitys(mandatory_facility_uuids);
                const facility_dam_uuids = fd.map(facility => facility.uuid);
                setFacilityDams(facility_dam_uuids);
                setDepartment(dp.name);

                // 初回表示および、編集モードの場合に初期値設定
                const isInputterStatus = status === APPROVAL_STATUS.REMAND || status === APPROVAL_STATUS.INIT;
                let processedCheckResult = cr;

                if (isInputterStatus && eq[`initial_report_uuid`] === null) {
                    // 該当施設が各基準の震度以上の場合に、初期値設定     
                    processedCheckResult = cr.map((row) => ({
                        ...row,
                        has_anomary: shouldValidateSituation(row.facility_uuid, row.intensity, facility_dam_uuids) && row.has_anomary === 0 ? 1 : row.has_anomary,
                        has_impact: shouldValidateSituation(row.facility_uuid, row.intensity, facility_dam_uuids) && row.has_impact === 0 ? 1 : row.has_impact,
                        has_media_response: shouldValidateSituation(row.facility_uuid, row.intensity, facility_dam_uuids) && row.has_media_response === 0 ? 1 : row.has_media_response,
                    }));
                }
                setCheckResult(processedCheckResult);

                if (status === APPROVAL_STATUS.APPROVED && !eq.initial_report_sent_at) {
                    const name = encodeURIComponent(currentUser?.name || '');
                    const dept = encodeURIComponent(dp?.name || '');
                    const mail = await fetchData(`api/get_mail_initial_report?user_name=${name}&user_department=${dept}&earthquake_uuid=${uuid}`);
                    setMailInfo(mail);
                }
            } catch (e) {
                console.error('fetch error:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [uuid, currentUser]);

    // taskStatus永続化
    useEffect(() => localStorage.setItem("taskStatus", String(taskStatus)), [taskStatus]);

    // 作成フラグリセット
    useEffect(() => setCreateComplete(false), [earthquake, checkResult]);

    // 返すべき値を返す
    return {
        earthquake,
        setEarthquake,
        checkResult,
        setCheckResult,
        approval,
        reportDetailList,
        mailInfo,
        loading,
        open,
        setOpen,
        isChecked,
        setIsChecked,
        createComplete,
        setCreateComplete,
        mandatoryFacilitys,
        setMandatoryFacilitys,
        facilityDams,
        setFacilityDams,
        taskStatus,
        setTaskStatus,
        approvalStatus,
        isInputter,
        isReviewer,
        isReadOnly,
        isApprovedNotSent,
        isSent,
        isValidTask,
        handleRestart,
        department
    };
};